import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {DataFrame, DataFrameRow, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import pick from 'util/pick';

import isSystemColumn from './isSystemColumn';

type AggregationFunction = (rows: Array<DataFrameRow>, partitionValue: string) => DataFrameRow;

/**
 * Aggregate a data frame's rows by mapping the rows into partitions, then aggregating each partition of
 * points into a single row of a new row type. System columns are carried over into the new data frame and
 * their values in the aggregated rows will be set using the values of the first row in each partition.
 *
 * @param dataFrame The data frame to aggregate
 * @param partitionFunction Callback used to partition out groups of rows
 * @param aggregationFunction Callback used to aggregate a group of rows into a single row
 * @param newColumnDefinitions Array of column definitions for the aggregated data frame
 */
export default function aggregateRows(
  dataFrame: DataFrame,
  partitionFunction: (row: DataFrameRow) => string,
  aggregationFunction: AggregationFunction,
  newColumnDefinitions: Array<DataFrameColumnDef>
): DataFrame {
  const systemColumns = dataFrame.columns.filter((column) => isSystemColumn(column));

  return {
    ...dataFrame,
    columns: [...newColumnDefinitions, ...systemColumns],
    rows: Object.entries(groupBy(dataFrame.rows, partitionFunction)).map(([partitionValue, rows]) => {
      assert(rows.length > 0, 'Unexpected empty partition');

      return {
        ...pick(rows[0], ...systemColumns.map((column) => column.systemName)),
        ...aggregationFunction(rows, partitionValue),
      };
    }),
  };
}
